import * as Sentry from '@sentry/react';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { StreamChat } from 'stream-chat';

type StreamApiErrorBody = {
  code?: number;
  message?: string;
  StatusCode?: number;
  duration?: string;
  more_info?: string;
};

const sanitizeHeaders = (
  headers: InternalAxiosRequestConfig['headers'] | AxiosResponse['headers'],
): Record<string, unknown> => {
  const rawHeaders = Object.fromEntries(Object.entries(headers ?? {}));

  delete rawHeaders.Authorization;
  delete rawHeaders.authorization;
  delete rawHeaders['Stream-Auth-Type'];
  delete rawHeaders['stream-auth-type'];

  return rawHeaders;
};

const isAxiosError = <TResponseBody>(error: unknown): error is AxiosError<TResponseBody> => {
  return typeof error === 'object' && error !== null && 'isAxiosError' in error;
};

const getStreamErrorTitle = (error: AxiosError) => {
  const method = error.config?.method?.toUpperCase() ?? 'REQUEST';
  const url = error.config?.url ?? 'unknown-url';
  const status = error.response?.status;

  if (!status) {
    return `GetStream API network error: ${method} ${url}`;
  }

  return `GetStream API error ${status}: ${method} ${url}`;
};

export const installStreamSentryInterceptors = (client: StreamChat) => {
  client.axiosInstance.interceptors.request.use((config) => {
    Sentry.addBreadcrumb({
      category: 'stream.api.request',
      level: 'info',
      message: `${config.method?.toUpperCase() ?? 'REQUEST'} ${config.url ?? ''}`,
      data: {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        params: config.params,
        body: config.data,
      },
    });

    return config;
  });

  client.axiosInstance.interceptors.response.use(
    (response) => {
      Sentry.addBreadcrumb({
        category: 'stream.api.response',
        level: 'info',
        message: `${response.status} ${response.config.url ?? ''}`,
        data: {
          method: response.config.method,
          url: response.config.url,
          status: response.status,
        },
      });

      return response;
    },
    (error: unknown) => {
      if (isAxiosError<StreamApiErrorBody>(error)) {
        const sentryError = new Error(getStreamErrorTitle(error));

        sentryError.name = 'GetStreamApiError';
        sentryError.cause = error;

        Sentry.captureException(sentryError, {
          tags: {
            sdk: 'getstream',
            api: 'stream-chat',
            status: String(error.response?.status ?? 'network_error'),
          },
          contexts: {
            stream_request: {
              method: error.config?.method,
              url: error.config?.url,
              baseURL: error.config?.baseURL,
              params: error.config?.params,
              body: error.config?.data,
              headers: error.config?.headers ? sanitizeHeaders(error.config.headers) : undefined,
            },
            stream_response: {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              headers: error.response?.headers ? sanitizeHeaders(error.response.headers) : undefined,
            },
          },
        });
      } else {
        Sentry.captureException(error, {
          tags: {
            sdk: 'getstream',
            api: 'stream-chat',
          },
        });
      }

      return Promise.reject(error);
    },
  );
};
